import { shortAddress } from '@plug-core/utils/shortAddress'
import { Box, List, Stack, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import addressMatcher, { AddressType } from '@plug-core/utils/addressMatcher'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaCopy, FaPenClip } from 'react-icons/fa6'
import { TransactionListItem } from '@components/TransactionList/TransactionListItem'
import { TransactionActivity } from '@plug-core/interfaces/transactionActivity'
import useWallet from '@plug-core/hooks/useWallet'
import LedgerScannerClient, { TransactionKind } from '@plug-core/clients/LedgerScannerClient'
import { logger } from '@plug-core/logger'
import { useTranslation } from 'react-i18next'
import { RainbowAvatar } from '@components/RainbowAvatar/RainbowAvatar'
import BottomSheet from '@components/BottomSheet'
import ContactForm from '@modules/ContactForm'
import EmptyList from '@components/EmptyList'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import { useContacts } from '@plug-core/state'
import Touchable from '@components/Touchable'

const getLogoForAddressType = (address: AddressType) => {
  switch (address) {
    case AddressType.PrincipalId:
    case AddressType.AccountId:
      return getChainIcon('icp')
    default:
      return getChainIcon(AddressType[address].toLowerCase())
  }
}

const Transactions = ({ transactions }: { transactions: TransactionActivity[] | undefined }) => {
  const { t } = useTranslation()

  if (!transactions) {
    return null
  } else if (transactions.length) {
    return (
      <Stack spacing={0.5} mx={1}>
        <Typography variant="h6" px={2}>
          {t('tokenDetails.lastTransactions')}
        </Typography>
        <Stack component={List} spacing={1}>
          {transactions.map((transaction) => (
            <TransactionListItem key={transaction.transactionId} transaction={transaction} />
          ))}
        </Stack>
      </Stack>
    )
  } else {
    return (
      <Box mt={15}>
        <EmptyList
          emoji="🥹"
          title={t('transactionHistory.empty')}
          description={t('transactionHistory.emptyMessage')}
        />
      </Box>
    )
  }
}

const ContactDetails = () => {
  const { updateContact, getContactByAddress } = useContacts()
  const { wallet } = useWallet()
  const location = useLocation()
  const navigate = useNavigate()

  const [transactions, setTransactions] = useState<TransactionActivity[]>()
  const [contactModalVisible, setContactModalVisible] = useState(false)
  const [emojisPickerVisible, setEmojisPickerVisible] = useState(false)
  const [address, setAddress] = useState(location.state.address)

  const contact = getContactByAddress(address)

  useEffect(() => {
    if (!contact) return

    LedgerScannerClient.fetchTransactions(wallet, {
      types: [TransactionKind.Transfer],
      pageSize: 5,
      receiver: contact.address,
    })
      .then(setTransactions)
      .catch(logger.debug)
  }, [wallet.principalId, contact?.address])

  useEffect(() => {
    if (!contact) navigate(-1)
  }, [contact?.address])

  if (!contact) return null

  const selectEmoji = (avatar: string) => {
    updateContact(contact.address, { avatar })
    setEmojisPickerVisible(false)
  }

  const addressType = addressMatcher(contact.address)

  return (
    <Box
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <Stack padding={3} alignItems="center">
        <Touchable onClick={() => setEmojisPickerVisible(true)}>
          <Stack position="relative">
            <RainbowAvatar size={100} avatar={contact.avatar} />
            {addressType !== AddressType.Invalid && (
              <img
                src={getLogoForAddressType(addressType)}
                alt={`${addressType} logo`}
                style={{
                  position: 'absolute',
                  bottom: -2,
                  right: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                }}
              />
            )}
          </Stack>
        </Touchable>

        <Touchable scaleFactor={0.02} onClick={() => setContactModalVisible(true)}>
          <Stack direction="row" alignItems="center" mt={2}>
            <Typography variant="h4" fontWeight={700} fontSize={30} mr={1.5}>
              {contact.name}
            </Typography>

            <FaPenClip size={16} />
          </Stack>
        </Touchable>

        <Touchable
          scaleFactor={0.03}
          onClick={() => navigator.clipboard.writeText(contact.address)}
        >
          <Stack
            direction="row"
            alignItems="center"
            borderRadius={3}
            mt={2}
            px={2.25}
            py={1.25}
            bgcolor="rgba(25, 118, 210, 0.08)"
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body2" fontFamily="monospace" fontWeight={500} mr={2}>
              {shortAddress(contact.address, { leftSize: 16 })}
            </Typography>

            <FaCopy />
          </Stack>
        </Touchable>
      </Stack>

      <EmojiPicker
        open={emojisPickerVisible}
        width={260}
        height={250}
        theme={Theme.DARK}
        style={{
          position: 'absolute',
          top: '30%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        previewConfig={{ showPreview: false }}
        onEmojiClick={(emoji) => selectEmoji(emoji.emoji)}
        skinTonesDisabled
        searchDisabled
      />

      <Transactions transactions={transactions} />

      <BottomSheet
        visible={contactModalVisible}
        onBackdropClick={() => setContactModalVisible(false)}
      >
        <ContactForm
          address={contact.address}
          onSave={(newAddress) => setAddress(newAddress)}
          close={() => setContactModalVisible(false)}
        />
      </BottomSheet>
    </Box>
  )
}

export default ContactDetails
